// ─── humanize.js — friendly sentence builder for audit_log rows ──────────
// Shared between Profile's AuditLogTab (full activity log) and
// ChangelogPanel (navbar side-panel). Takes a server-shaped event row
// ({ action, entity_type, entity_id, details: { path, from, to, op }, … })
// and returns ONE plain-English sentence the user can read at a glance:
//
//   "Alice changed Delivery Cost of product 'Acme 1' (Amazon) from $5 to $6."
//
// Never leaks raw JSON, [object Object], UUIDs, or "marketplace.foo"
// paths into the rendered output.

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function fmtMonthKey(k) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(k || ""));
  if (!m) return String(k || "");
  const idx = Math.max(0, Math.min(11, Number(m[2]) - 1));
  return `${MONTH_NAMES[idx]} ${m[1]}`;
}

export function actorName(ev) {
  const email = ev.email || "";
  if (!email) return "Someone";
  const local = email.split("@")[0] || email;
  const pretty = local.replace(/[._\-+]+/g, " ").trim();
  return pretty.replace(/\b\w/g, c => c.toUpperCase()) || email;
}

// Title-case + nice common-word fixes for raw field names. Always returns
// something readable, e.g. "deliveryCost" → "Delivery Cost".
function prettifyFieldLabel(raw) {
  if (!raw) return "the value";
  const replacements = {
    "pct": "%", "cogs": "COGS", "ppc": "PPC", "tacos": "TACOS",
    "racos": "RACOS", "acos": "ACOS", "b2c": "B2C", "b2b": "B2B",
    "cbm": "CBM", "fbm": "FBM", "id": "", "ids": "", "api": "API",
  };
  return String(raw)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_\-]+/g, " ")
    .trim()
    .split(/\s+/)
    .map(w => {
      const k = w.toLowerCase();
      if (replacements[k] !== undefined) return replacements[k];
      return k.charAt(0).toUpperCase() + k.slice(1);
    })
    .filter(Boolean)
    .join(" ");
}

// Money / percent / count heuristics so values render with a unit.
const PCT_FIELDS = /(percent|share|rate|pct|growth|reserve rate|duty %|margin|roi|racos|tacos|acos|ppc)/i;
const MONEY_FIELDS = /(amount|cost|payout|limit|credit|reserve held|delivery|cogs|inspection|insurance|price|profit|revenue|fee|spend)/i;
const COUNT_FIELDS = /(stock|units|qty|containers|count|sales)/i;

function objectLabel(obj) {
  if (!obj || typeof obj !== "object") return null;
  if (obj.name) return String(obj.name);
  if (obj.what) return String(obj.what);
  if (obj.label) return String(obj.label);
  if (obj.title) return String(obj.title);
  if (obj.value != null && obj.startDate) {
    return `${obj.value} starting ${fmtDateFriendly(obj.startDate)}`;
  }
  if (obj.pct != null) return `${obj.pct}%`;
  if (Array.isArray(obj.productIds)) return `group of ${obj.productIds.length} products`;
  return null;
}

function fmtDateFriendly(s) {
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ""));
  if (iso) {
    const d = new Date(s);
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString(undefined, { day:"numeric", month:"short", year:"numeric" });
    }
  }
  return String(s || "");
}

function fmtValHuman(v, fieldLabel) {
  if (v == null || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) {
    if (v.length === 0) return "none";
    const items = v.map(x => x == null ? null
      : (typeof x === "object" ? objectLabel(x) : String(x)));
    const allLabeled = items.every(s => !!s);
    if (allLabeled && v.length <= 3) return items.join(", ");
    if (allLabeled) return `${items.slice(0, 2).join(", ")} +${v.length - 2} more`;
    return `${v.length} ${v.length === 1 ? "item" : "items"}`;
  }
  if (typeof v === "object") {
    const label = objectLabel(v);
    if (label) return label;
    const keys = Object.keys(v).filter(k => v[k] != null && v[k] !== "");
    if (keys.length === 0) return "empty";
    if (keys.length <= 3) return keys.map(k => `${prettifyFieldLabel(k)}: ${fmtValHuman(v[k], k)}`).join(", ");
    return `${keys.length} fields`;
  }
  const s = String(v);
  const asNum = Number(s);
  if (typeof v === "number" || (s !== "" && !isNaN(asNum) && /^-?\d/.test(s))) {
    const fl = fieldLabel || "";
    const n = asNum;
    if (PCT_FIELDS.test(fl)) return `${n}%`;
    if (MONEY_FIELDS.test(fl)) return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    if (COUNT_FIELDS.test(fl)) return n.toLocaleString();
    return n.toLocaleString();
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return fmtDateFriendly(s);
  if (/^[0-9a-f]{8}-[0-9a-f]{4}/i.test(s)) return s.slice(0, 6) + "…";
  return s.length > 60 ? s.slice(0, 57) + "…" : s;
}

function entityNameParts(ev) {
  const path = ev?.details?.path || "";
  const head = (path.split(/\s*·\s*/)[0] || "").trim();
  const t = ev.entity_type;
  const stripped = head.replace(/^[A-Z][A-Za-z _]+?\s+/, "");
  if (t === "month") {
    return { primary: fmtMonthKey(stripped || ev.entity_id), secondary: null };
  }
  if (t === "expense" || t === "income_item") {
    const [mkey, name] = stripped.split("/");
    if (mkey && name) return { primary: name.trim(), secondary: fmtMonthKey(mkey.trim()) };
    return { primary: stripped || "(unnamed)", secondary: null };
  }
  if (t === "product") {
    const [mkt, prod] = stripped.split("/");
    if (mkt && prod) return { primary: prod.trim(), secondary: mkt.trim() };
    return { primary: stripped || "(unnamed)", secondary: null };
  }
  if (t === "metric_history") {
    const parts = String(ev.entity_id || "").split("/");
    return { primary: parts[1] || "metric", secondary: parts[2] ? fmtDateFriendly(parts[2]) : null };
  }
  if (t === "payment_term" || t === "container_batch" || t === "container_line") {
    const [order] = stripped.split("/");
    return { primary: stripped || "(unnamed)", secondary: order ? `order ${order}` : null };
  }
  if (t === "delivery_override" || t === "order_expense_paid" || t === "recurring_override") {
    const [mkey] = stripped.split("/");
    return { primary: stripped || "(unnamed)", secondary: mkey ? fmtMonthKey(mkey) : null };
  }
  return { primary: stripped || (ev.entity_id ? String(ev.entity_id).slice(0, 6) + "…" : "(unnamed)"), secondary: null };
}

function entityNoun(type) {
  const m = {
    brand: "the brand",
    month: "month", marketplace: "marketplace", product: "product",
    order: "order", payment_term: "payment term",
    container_batch: "container batch", container_line: "container line",
    expense: "expense", income_item: "income item",
    decision: "decision", pool: "inventory pool",
    modifier: "modifier", fbm_setting: "FBM profile",
    delivery_override: "delivery override",
    order_expense_paid: "order-expense status",
    recurring: "recurring item",
    recurring_override: "recurring override",
    metric_history: "history entry",
  };
  return m[type] || (type ? type.replace(/_/g, " ") : "item");
}

function entityPhrase(ev) {
  const { primary, secondary } = entityNameParts(ev);
  const noun = entityNoun(ev.entity_type);
  if (ev.entity_type === "brand") return "the brand";
  if (ev.entity_type === "month") return `${noun} ${primary}`;
  const base = primary && primary !== "(unnamed)" ? `${noun} '${primary}'` : noun;
  if (secondary) return `${base} (${secondary})`;
  return base;
}

function legacySentence(ev) {
  const actor = actorName(ev);
  switch (ev.action) {
    case "member_invited": {
      const target = (ev.detail || "").split("—")[0].trim();
      return `${actor} invited ${target || "a new team member"} to the account.`;
    }
    case "member_accepted":     return `${actor} joined the team.`;
    case "member_removed":      return `${actor} removed a team member.`;
    case "permissions_updated": return `${actor} updated team permissions.`;
    case "brand_created":       return `${actor} created a new brand${ev.detail ? ` — "${ev.detail}"` : ""}.`;
    case "brand_renamed":       return `${actor} renamed a brand${ev.detail ? ` to "${ev.detail}"` : ""}.`;
    case "whitelabel_updated":  return `${actor} updated the white-label branding.`;
    case "stock_overridden":    return `${actor} manually overrode current stock${ev.detail ? ` — ${ev.detail}` : ""}.`;
    case "order_deleted":       return `${actor} deleted an order${ev.detail ? ` (${ev.detail})` : ""}.`;
    case "decision_deleted":    return `${actor} deleted a decision${ev.detail ? ` (${ev.detail})` : ""}.`;
    case "settings_reset":      return `${actor} reset the brand settings.`;
    default: return null;
  }
}

export function humanizeEvent(ev) {
  if (!ev) return "";
  const legacy = legacySentence(ev);
  if (legacy) return legacy;
  const actor = actorName(ev);
  const det = ev.details || {};
  const op = det.op;
  const action = ev.action || "";
  const path = det.path || "";
  const segs = path.split(/\s*·\s*/).filter(Boolean);
  const rawField = (segs[segs.length - 1] || "").trim();
  const fieldLabel = prettifyFieldLabel(rawField.replace(/^Brand\b/i, "").trim() || "value");
  const phrase = entityPhrase(ev);

  if (ev.entity_type === "metric_history") {
    const parts = String(ev.entity_id || "").split("/");
    const colId = parts[1] || "metric";
    const startDate = parts[2] ? fmtDateFriendly(parts[2]) : null;
    const colNice = prettifyFieldLabel(colId);
    const head = (segs[0] || "").replace(/^Product\s+/i, "");
    const [mkt, prod] = head.split("/");
    const prodPhrase = prod
      ? `product '${prod.trim()}'${mkt ? ` (${mkt.trim()})` : ""}`
      : "a product";
    if (op === "add") {
      const v = det.to && typeof det.to === "object" ? det.to.value : det.to;
      return `${actor} added a ${colNice} history entry for ${prodPhrase}: ${fmtValHuman(v, colId)}${startDate ? ` starting ${startDate}` : ""}.`;
    }
    if (op === "remove") {
      const v = det.from && typeof det.from === "object" ? det.from.value : det.from;
      return `${actor} removed a ${colNice} history entry from ${prodPhrase}: ${fmtValHuman(v, colId)}${startDate ? ` (was starting ${startDate})` : ""}.`;
    }
    const fromV = det.from && typeof det.from === "object" ? det.from.value : det.from;
    const toV = det.to && typeof det.to === "object" ? det.to.value : det.to;
    return `${actor} edited the ${colNice} history entry for ${prodPhrase}${startDate ? ` on ${startDate}` : ""}: ${fmtValHuman(fromV, colId)} → ${fmtValHuman(toV, colId)}.`;
  }

  if (op === "add" || action.endsWith(".add")) {
    const what = (typeof det.to === "string" && det.to) ? det.to : null;
    return `${actor} added a new ${entityNoun(ev.entity_type)}${what ? ` — "${what}"` : ""}.`;
  }
  if (op === "remove" || action.endsWith(".remove")) {
    const what = (typeof det.from === "string" && det.from) ? det.from : null;
    return `${actor} removed ${entityNoun(ev.entity_type)}${what ? ` "${what}"` : ""}.`;
  }

  if (typeof det.from === "boolean" || typeof det.to === "boolean") {
    const turnedOn = det.to === true;
    const verb = turnedOn ? "turned on" : "turned off";
    if (ev.entity_type === "brand") return `${actor} ${verb} ${fieldLabel} for the brand.`;
    return `${actor} ${verb} ${fieldLabel} on ${phrase}.`;
  }

  const from = fmtValHuman(det.from, rawField);
  const to = fmtValHuman(det.to, rawField);
  if (ev.entity_type === "brand") {
    return `${actor} changed ${fieldLabel} from ${from} to ${to}.`;
  }
  return `${actor} changed ${fieldLabel} of ${phrase} from ${from} to ${to}.`;
}
